import React, { useState, useEffect } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	List,
	ListItemButton,
	ListItemAvatar,
	Avatar,
	ListItemText,
	TextField,
	InputAdornment,
	CircularProgress,
	Box,
	Typography,
	IconButton,
	Skeleton,
	ListItem,
	Badge,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { useAuth } from "@/hooks/auth";

interface User {
	id: string;
	firstName: string;
	lastName: string;
	avatar: string;
	email: string;
	isOnline?: boolean;
	username: string;
}

interface UserSelectDialogProps {
	open: boolean;
	onClose: () => void;
	onSelectUser: (userId: string) => void;
}

export const UserSelectDialog: React.FC<UserSelectDialogProps> = ({
	open,
	onClose,
	onSelectUser,
}) => {
	const theme = useTheme();
	const { user: currentUser } = useAuth();
	const [users, setUsers] = useState<User[]>([]);
	const [loading, setLoading] = useState(false);
	const [search, setSearch] = useState("");

	useEffect(() => {
		if (!open) return;

		const fetchUsers = async () => {
			setLoading(true);
			try {
				const res = await fetch(
					`${process.env.REACT_APP_API_URL}/users?limit=50&search=${encodeURIComponent(search)}`,
					{
						headers: {
							Authorization: `Bearer ${localStorage.getItem("token")}`,
						},
					},
				);
				const data = await res.json();
				// Assuming data.data.users based on users.tsx snippet
				const fetchedUsers = data?.data?.users || data?.users || [];
				// Filter out current user from the list
				setUsers(fetchedUsers.filter((u: User) => u.id !== currentUser?.id));
			} catch (err) {
				console.error("Failed to fetch users", err);
			} finally {
				setLoading(false);
			}
		};

		const timer = setTimeout(fetchUsers, 300); // debounce search
		return () => clearTimeout(timer);
	}, [open, search, currentUser?.id]);

	return (
		<Dialog 
			open={open} 
			onClose={onClose} 
			fullWidth 
			maxWidth="xs"
			PaperProps={{
				sx: {
					borderRadius: 3,
					backgroundImage: "none",
					backgroundColor: theme.palette.mode === 'dark' ? 'rgba(30, 30, 30, 0.85)' : 'rgba(255, 255, 255, 0.95)',
					backdropFilter: "blur(16px)",
					boxShadow: theme.shadows[16],
				}
			}}
		>
			<DialogTitle
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					pb: 1,
				}}
			>
				<Typography variant="h6" fontWeight={600}>
					Шинэ чат нээх
				</Typography>
				<IconButton size="small" onClick={onClose} sx={{ color: "text.secondary", backgroundColor: "action.hover" }}>
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>
			<DialogContent
				sx={{ p: 0, display: "flex", flexDirection: "column", height: 400 }}
			>
				<Box sx={{ px: 2, pt: 1, pb: 1 }}>
					<TextField
						fullWidth
						size="small"
						placeholder="Хэрэглэгч хайх..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon fontSize="small" color="action" />
								</InputAdornment>
							),
							sx: { 
								borderRadius: 2, 
								backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.02)',
								"& fieldset": { borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)' }
							}
						}}
					/>
				</Box>

				<List 
					sx={{ 
						flexGrow: 1, 
						overflowY: "auto",
						"&::-webkit-scrollbar": { width: 6 },
						"&::-webkit-scrollbar-thumb": {
							backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)",
							borderRadius: 3,
							"&:hover": {
								backgroundColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.25)",
							},
						},
						px: 1,
					}}
				>
					{loading && users.length === 0 ? (
						Array.from({ length: 5 }).map((_, idx) => (
							<ListItem key={idx} disablePadding dense sx={{ mb: 0.5 }}>
								<ListItemButton sx={{ borderRadius: 2 }}>
									<ListItemAvatar>
										<Skeleton variant="circular" width={40} height={40} />
									</ListItemAvatar>
									<ListItemText
										primary={<Skeleton variant="text" width="60%" sx={{ mb: 0.5 }} />}
										secondary={<Skeleton variant="text" width="40%" />}
									/>
								</ListItemButton>
							</ListItem>
						))
					) : users.length === 0 ? (
						<Box sx={{ p: 4, textAlign: "center", color: "text.secondary", mt: 4 }}>
							<SearchIcon sx={{ fontSize: 48, opacity: 0.2, mb: 1 }} />
							<Typography variant="body2">Илэрц олдсонгүй</Typography>
						</Box>
					) : (
						users.map((u) => (
							<ListItem key={u.id} disablePadding dense sx={{ mb: 0.5 }}>
								<ListItemButton
									onClick={() => onSelectUser(u.id)}
									sx={{ borderRadius: 2 }}
								>
									<ListItemAvatar>
										<Badge
											overlap="circular"
											anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
											variant="dot"
											sx={{
												"& .MuiBadge-badge": {
													backgroundColor: u.isOnline ? "success.main" : "text.disabled",
													boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
													width: 10,
													height: 10,
													borderRadius: "50%",
												}
											}}
										>
											<Avatar src={u.avatar} alt={u.firstName} sx={{ width: 40, height: 40 }}>
												{u.firstName?.[0]}
											</Avatar>
										</Badge>
									</ListItemAvatar>
									<ListItemText
										primary={
											<Typography variant="subtitle2" color="text.primary" fontWeight="600" noWrap>
												{[u.firstName, u.lastName]
													.filter((n) => n && n !== "undefined")
													.join(" ") ||
												(u.username !== "undefined" && u.username ? u.username : "") ||
												u.email ||
												"Unknown User"}
											</Typography>
										}
										secondary={
											<Typography variant="caption" color="text.secondary" noWrap>
												@{u.username !== "undefined" && u.username ? u.username : (u.email || "Unknown")}
											</Typography>
										}
									/>
								</ListItemButton>
							</ListItem>
						))
					)}
				</List>
			</DialogContent>
		</Dialog>
	);
};
